/**
 * Website Management — the ONE entry point for managing the public website.
 *
 * Sprint: TREATX-WEBSITE-MGMT-01 (Stage 1)
 *
 * This is NOT a fifth CMS. It is a thin, task-oriented shell that renders the
 * FOUR existing management surfaces (plus the authenticated V3 draft review)
 * in place, each still talking to its own canonical authority:
 *
 *   Homepage          → AdminLandingCMS      (/api/admin/landing-content)
 *                     → AdminLandingStats    (/api/admin/platform-stats)
 *   Provider Benefits → AdminProviderBenefitsCMS (/api/admin/site-content/*)
 *   Preview & Publish → AdminHomepageVersion (/api/admin/homepage-version)
 *                     → V3DraftReview        (authenticated draft preview)
 *
 * No content is copied, mirrored or re-stored here. The tab is kept in the URL
 * (`?tab=`) so deep links, bookmarks and browser back/forward all work.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Home, Building2, Rocket, ExternalLink, Layers } from 'lucide-react';

import AdminLandingCMS from './AdminLandingCMS';
import AdminLandingStats from './AdminLandingStats';
import AdminProviderBenefitsCMS from './AdminProviderBenefitsCMS';
import AdminHomepageVersion from './AdminHomepageVersion';
import V3DraftReview from './V3DraftReview';
import AdminV3Homepage from './AdminV3Homepage';

const TABS = [
  {
    id: 'homepage',
    label: 'Homepage (V1 — live)',
    icon: Home,
    blurb: 'The published V1 homepage: hero, banners, page sections, images and the statistics strip.',
  },
  {
    id: 'homepage-v3',
    label: 'Homepage (V3 — candidate)',
    icon: Layers,
    blurb: 'Every editorial section of the V3 candidate, section by section. Editing here cannot touch the live V1 homepage.',
  },
  {
    id: 'provider-benefits',
    label: 'Provider Benefits',
    icon: Building2,
    blurb: 'Hero, benefit rows, closing call-to-action and their images.',
  },
  {
    id: 'preview-publish',
    label: 'Preview & Publish',
    icon: Rocket,
    blurb: 'Choose the live homepage version, review the V3 candidate, publish.',
  },
];

const VALID = TABS.map((t) => t.id);

const SectionHeading = ({ title, description }) => (
  <div className="mb-3 border-b border-slate-200 pb-2">
    <h2 className="text-base font-semibold text-slate-900">{title}</h2>
    {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
  </div>
);

const WebsiteManagement = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const requested = params.get('tab');
  const active = VALID.includes(requested) ? requested : 'homepage';

  // Replace (not push) when normalising an unknown tab so the back button
  // still returns to wherever the owner came from.
  const selectTab = (id) => setParams({ tab: id }, { replace: false });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="website-management">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost" size="sm"
                onClick={() => navigate('/treatx-admin/dashboard')}
                data-testid="wm-back"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Website Management</h1>
                <p className="mt-0.5 text-sm text-slate-500">
                  Everything shown on the public website, in one place.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => window.open('/#/', '_blank')}
              data-testid="wm-view-site"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View live site
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2" role="tablist" data-testid="wm-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const on = active === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={on}
                  onClick={() => selectTab(t.id)}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    on
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`wm-tab-${t.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-slate-500" data-testid="wm-tab-blurb">
            {TABS.find((t) => t.id === active)?.blurb}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {active === 'homepage' && (
          <div className="space-y-8" data-testid="wm-panel-homepage">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900"
                 data-testid="wm-v1-scope-note">
              <span className="font-semibold">This tab edits the LIVE V1 homepage.</span>{' '}
              Records without a version badge belong to V1. The V3 candidate has its own
              tab — <span className="font-semibold">Homepage (V3 — candidate)</span> — and the two
              never share a record.
            </div>
            <section>
              <SectionHeading
                title="Page content"
                description="Hero, announcements, trust and feature blocks, and their images."
              />
              <AdminLandingCMS embedded />
            </section>
            <section>
              <SectionHeading
                title="Statistics strip"
                description="Figures stay calculated from live platform data — you control their labels, order and visibility."
              />
              <AdminLandingStats embedded />
            </section>
          </div>
        )}

        {active === 'homepage-v3' && (
          <div className="space-y-4" data-testid="wm-panel-homepage-v3">
            <SectionHeading
              title="V3 candidate — section by section"
              description="Each section of the V3 homepage, in page order. A section with no record keeps its built-in copy until you adopt it."
            />
            <AdminV3Homepage embedded />
          </div>
        )}

        {active === 'provider-benefits' && (
          <div className="space-y-4" data-testid="wm-panel-provider-benefits">
            {/* V1V3-SSOT-01 — two authorities can supply the provider section
                heading. Stated here so an admin is never left editing the
                field that loses. */}
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs leading-relaxed text-sky-900"
                 data-testid="wm-provider-authority-note">
              <span className="font-semibold">Where this content appears.</span>{' '}
              These blocks are the authority for the <span className="font-semibold">Provider Benefits page</span>,
              and the same hero title and row headlines are reused by the
              <span className="font-semibold"> “For healthcare providers”</span> section of the V3 homepage.
              If the hero title here is set, it <span className="font-semibold">overrides</span> the
              Landing CMS <code className="rounded bg-white px-1">provider_cta</code> title on V3 —
              clear this field if you want the Landing CMS copy to show instead.
              Rows follow their own <span className="font-semibold">visibility</span> and{' '}
              <span className="font-semibold">order</span> on both pages; the first six visible rows
              fill the V3 capability cards.
            </div>
            <AdminProviderBenefitsCMS embedded />
          </div>
        )}

        {active === 'preview-publish' && (
          <div className="space-y-8" data-testid="wm-panel-preview-publish">
            <section>
              <SectionHeading
                title="Homepage version"
                description="Which version the public sees, release readiness and publication."
              />
              <AdminHomepageVersion embedded />
            </section>
            <section>
              <SectionHeading
                title="V3 candidate review"
                description="The full V3 draft, visible to signed-in reviewers only. Reviewing never publishes it."
              />
              <V3DraftReview embedded />
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteManagement;
